/**
 * `<SettingsContextVariablesRoute>` — `/w/:slug/context-variables`
 *
 * Workspace-level catalog of declared `{{context.*}}` variables
 * (context-variables unit, PR-C). Operators declare the keys a host page may
 * push live per conversation; the catalog drives both the ingress allowlist
 * and the prompt editor's `@`-mention picker (PR-D).
 *
 * `type = enum` reveals the enum-values editor; `key` is immutable after
 * creation (renaming would silently break prompts that already reference it —
 * see contracts § 5.1†).
 */

import { useMemo, useState } from "react";

import { useWorkspace } from "@cometchat-ai/auth/client";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  TableBody,
  TableHeader,
  Textarea,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { Braces, Check, MoreHorizontal, Pencil, Plus, Trash2, X } from "../components/icons";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { SearchInput } from "../components/search-input";
import { ListSkeleton } from "../components/list-skeleton";
import {
  useContextVariables,
  useCreateContextVariable,
  useDeleteContextVariable,
  useUpdateContextVariable,
  type ContextVariableType,
  type WorkspaceContextVariable,
} from "../context-variables/api/context-variables";

const KEY_RE = /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*$/;

const INPUT_CLASS = "h-10 rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]";

interface FormState {
  key: string;
  label: string;
  description: string;
  type: ContextVariableType;
  required: boolean;
  sensitive: boolean;
  /** Comma/newline-separated; split on save. Only used when type = enum. */
  enumValues: string;
  exampleValue: string;
}

const EMPTY_FORM: FormState = {
  key: "",
  label: "",
  description: "",
  type: "string",
  required: false,
  sensitive: false,
  enumValues: "",
  exampleValue: "",
};

function toForm(v: WorkspaceContextVariable): FormState {
  return {
    key: v.key,
    label: v.label,
    description: v.description ?? "",
    type: v.type,
    required: v.required,
    sensitive: v.sensitive,
    enumValues: v.enumValues.join("\n"),
    exampleValue: v.exampleValue ?? "",
  };
}

function parseEnumValues(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      {type}
    </span>
  );
}

function FlagChip({ kind }: { kind: "required" | "sensitive" }) {
  const color = kind === "sensitive" ? "var(--color-warning)" : "var(--color-accent)";
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs"
      style={{ color, background: `color-mix(in oklch, ${color} 12%, transparent)` }}
    >
      {kind}
    </span>
  );
}

export function SettingsContextVariablesRoute() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;

  const listQuery = useContextVariables(slug);
  const createVar = useCreateContextVariable(slug);
  const updateVar = useUpdateContextVariable(slug);
  const deleteVar = useDeleteContextVariable(slug);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkspaceContextVariable | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [typeFilter, setTypeFilter] = useState<"all" | ContextVariableType>("all");
  const [search, setSearch] = useState("");

  const variables = listQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return variables.filter(
      (v) =>
        (typeFilter === "all" || v.type === typeFilter) &&
        (!q || v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q)),
    );
  }, [variables, typeFilter, search]);
  const isEnum = form.type === "enum";
  const enumList = parseEnumValues(form.enumValues);
  const keyValid = KEY_RE.test(form.key);
  const canSubmit =
    keyValid &&
    form.label.trim().length > 0 &&
    (!isEnum || enumList.length > 0) &&
    !createVar.isPending &&
    !updateVar.isPending;

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(v: WorkspaceContextVariable) {
    setEditing(v);
    setForm(toForm(v));
    setDialogOpen(true);
  }

  function handleSubmit() {
    const enumValues = isEnum ? enumList : [];
    const description = form.description.trim() || undefined;
    const exampleValue = form.exampleValue.trim() || undefined;

    if (editing) {
      updateVar.mutate(
        {
          id: editing.id,
          patch: {
            label: form.label.trim(),
            description,
            type: form.type,
            required: form.required,
            sensitive: form.sensitive,
            enumValues,
            exampleValue,
          },
        },
        {
          onSuccess: () => {
            toast.success("Context variable updated");
            setDialogOpen(false);
          },
          onError: () => toast.error("Could not update context variable"),
        },
      );
      return;
    }

    createVar.mutate(
      {
        key: form.key.trim(),
        label: form.label.trim(),
        description,
        type: form.type,
        required: form.required,
        sensitive: form.sensitive,
        enumValues,
        exampleValue,
      },
      {
        onSuccess: () => {
          toast.success("Context variable created");
          setDialogOpen(false);
        },
        onError: () => toast.error("Could not create context variable"),
      },
    );
  }

  return (
    <PageShell
      className="p-0"
      data-testid="settings-context-variables"
      description="Declare the live, per-conversation context a host page can send (e.g. the product the user is viewing). Declared keys resolve as {{context.<key>}} in prompts and gate what the host may push."
      actions={
        <Button
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          data-testid="context-variable-add"
          onClick={openCreate}
        >
          Add variable
        </Button>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-[18px] py-[18px]">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted p-0.5">
          {(["all", "string", "number", "boolean", "enum"] as const).map((tp) => (
            <button
              key={tp}
              type="button"
              onClick={() => setTypeFilter(tp)}
              data-active={typeFilter === tp}
              data-testid={`context-variable-filter-${tp}`}
              className="inline-flex h-6 items-center rounded-[5px] px-2.5 text-[13px] capitalize text-muted-foreground transition-colors hover:text-foreground data-[active=true]:bg-background data-[active=true]:text-foreground data-[active=true]:shadow-sm"
            >
              {tp}
            </button>
          ))}
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search variables…"
          data-testid="context-variables-search"
        />
      </div>

      {listQuery.isPending ? (
        <ListSkeleton
          rows={4}
          itemClassName="h-10 w-full"
          className="space-y-2 p-6"
          data-testid="context-variables-skeleton"
        />
      ) : listQuery.error ? (
        <p
          className="px-6 py-12 text-center text-[13px] text-destructive"
          data-testid="context-variables-error"
        >
          Could not load context variables.
        </p>
      ) : variables.length === 0 ? (
        <div className="px-6 py-12 text-center" data-testid="context-variables-empty">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
            <Braces className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-foreground">No context variables yet</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Add one to let a host page pass live context into prompts.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <p
          className="px-6 py-12 text-center text-[13px] text-muted-foreground"
          data-testid="context-variables-no-match"
        >
          No variables match these filters.
        </p>
      ) : (
        <DataGrid data-testid="context-variables-table">
          <TableHeader>
            <DataGridHeaderRow>
              <DataGridHead className="w-[220px] pl-6 pr-3">Key</DataGridHead>
              <DataGridHead>Label</DataGridHead>
              <DataGridHead className="w-[120px]">Type</DataGridHead>
              <DataGridHead className="w-[200px]">Flags</DataGridHead>
              <DataGridHead className="w-[88px] pr-6 text-right" divider={false}>
                Actions
              </DataGridHead>
            </DataGridHeaderRow>
          </TableHeader>
          <TableBody>
            {filtered.map((v) => (
              <DataGridRow key={v.id} data-testid={`context-variable-row-${v.key}`}>
                <DataGridCell className="pl-6 pr-3">
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {v.key}
                  </span>
                </DataGridCell>
                <DataGridCell className="truncate text-foreground">{v.label}</DataGridCell>
                <DataGridCell>
                  <TypeBadge type={v.type} />
                </DataGridCell>
                <DataGridCell>
                  {v.required || v.sensitive ? (
                    <div className="flex flex-wrap gap-1.5">
                      {v.required ? <FlagChip kind="required" /> : null}
                      {v.sensitive ? <FlagChip kind="sensitive" /> : null}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DataGridCell>
                <DataGridCell className="w-[88px] pr-6 text-right" divider={false}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label="Actions"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
                        data-testid={`context-variable-actions-${v.key}`}
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        className="gap-2 text-[13px]"
                        onClick={() => openEdit(v)}
                        data-testid={`context-variable-edit-${v.key}`}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="gap-2 text-[13px] text-destructive focus:!bg-[color:color-mix(in_oklch,var(--color-danger)_12%,transparent)] focus:!text-destructive"
                        disabled={deleteVar.isPending}
                        onClick={() =>
                          deleteVar.mutate(v.id, {
                            onSuccess: () => toast.success("Context variable deleted"),
                            onError: () => toast.error("Could not delete context variable"),
                          })
                        }
                        data-testid={`context-variable-delete-${v.key}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </DataGridCell>
              </DataGridRow>
            ))}
          </TableBody>
        </DataGrid>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg gap-0 rounded-3xl p-0 sm:rounded-3xl">
          <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
            <DialogTitle className="text-base font-medium tracking-[0.01em]">
              {editing ? "Edit context variable" : "Add context variable"}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">
              Define a typed value a host page can send per conversation. The key is permanent once
              created.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 pb-2">
            <div className="space-y-1.5">
              <Label htmlFor="cv-key" className="text-[13px] font-medium text-foreground">
                Key
              </Label>
              <Input
                id="cv-key"
                data-testid="context-variable-key-input"
                placeholder="e.g. productId or order.total"
                className={INPUT_CLASS}
                value={form.key}
                disabled={Boolean(editing)}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                A segment (productId) or dotted leaf path (order.total). Cannot be changed after
                creation.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cv-label" className="text-[13px] font-medium text-foreground">
                Label
              </Label>
              <Input
                id="cv-label"
                data-testid="context-variable-label-input"
                placeholder="Product ID"
                className={INPUT_CLASS}
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cv-description" className="text-[13px] font-medium text-foreground">
                Description (optional)
              </Label>
              <Textarea
                id="cv-description"
                data-testid="context-variable-description-input"
                placeholder="What this value represents"
                className="min-h-0 resize-none rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cv-type" className="text-[13px] font-medium text-foreground">
                Type
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v as ContextVariableType })}
              >
                <SelectTrigger
                  id="cv-type"
                  data-testid="context-variable-type-trigger"
                  className="h-10 rounded-md text-[13px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="enum">Enum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isEnum ? (
              <div className="space-y-1.5">
                <Label htmlFor="cv-enum" className="text-[13px] font-medium text-foreground">
                  Allowed values
                </Label>
                <Textarea
                  id="cv-enum"
                  data-testid="context-variable-enum-input"
                  placeholder="One per line (or comma-separated)"
                  className="min-h-0 resize-none rounded-md text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                  value={form.enumValues}
                  onChange={(e) => setForm({ ...form, enumValues: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Values the host may send for this enum.
                </p>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="cv-example" className="text-[13px] font-medium text-foreground">
                Example value (optional)
              </Label>
              <Input
                id="cv-example"
                data-testid="context-variable-example-input"
                placeholder="sku_42"
                className={INPUT_CLASS}
                value={form.exampleValue}
                onChange={(e) => setForm({ ...form, exampleValue: e.target.value })}
              />
            </div>

            <div className="rounded-md border border-border">
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="space-y-0.5">
                  <Label htmlFor="cv-required" className="text-[13px] font-medium text-foreground">
                    Required
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Flags a non-blocking trace when the host omits it. Never blocks a turn.
                  </p>
                </div>
                <Switch
                  id="cv-required"
                  data-testid="context-variable-required-switch"
                  checked={form.required}
                  onCheckedChange={(checked) => setForm({ ...form, required: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3">
                <div className="space-y-0.5">
                  <Label htmlFor="cv-sensitive" className="text-[13px] font-medium text-foreground">
                    Sensitive
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Redacts the value in logs and traces (treated as PII).
                  </p>
                </div>
                <Switch
                  id="cv-sensitive"
                  data-testid="context-variable-sensitive-switch"
                  checked={form.sensitive}
                  onCheckedChange={(checked) => setForm({ ...form, sensitive: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
            <DialogClose asChild>
              <Button
                variant="secondary"
                size="sm"
                className="h-9 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </DialogClose>
            <Button
              size="sm"
              className="h-9 flex-1 gap-1.5 rounded-md text-[13px]"
              data-testid="context-variable-submit"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {editing ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
